import type { DropStatus, DropTag, FirebaseVideo, WeeklyDrop } from "@/lib/firebase/videos";
import type { DevState } from "@/lib/dev-state";

const WEEKLY_DROPS_KEY = "rp.weeklyDrops.v1";
const LIBRARY_OWNED_KEY_PREFIX = "rp.libraryOwned.v1:";
const LIBRARY_NEXT_DROP_KEY = "rp.libraryNextDrop.v1";
const WEEKLY_DROPS_TTL_MS = 6 * 60 * 60 * 1000;

type CachedFirebaseVideo = {
  id: string;
  slug: string;
  title: string;
  thumbnailUrl: string | null;
  creatorImageUrl: string | null;
  publishedAtIso: string | null;
  durationLabel: string;
  creator: { id: string; name: string; slug: string; image?: string };
};

type CachedWeeklyDrop = {
  seriesId: string;
  week: number;
  cycle: number;
  slotIndex: number;
  slug: string;
  title: string;
  thumbnail: string | null;
  duration: string;
  releaseAt: string;
  tag: DropTag;
  status: DropStatus;
  video: CachedFirebaseVideo;
};

type WeeklyDropsCacheEntry = {
  version: 1;
  cachedAt: number;
  drops: CachedWeeklyDrop[];
};

export type LibraryFingerprintInput = {
  uid: string;
  isLegacyMember: boolean;
  membershipType?: "legacy" | "v2";
  creators: "all" | string[];
  v2Tier?: string | null;
  subscribedAtMs: number | null;
  devState: DevState;
  subscribedWeeks: number | null;
};

export type LibraryOwnedCacheEntry = {
  version: 1;
  fingerprint: string;
  cachedAt: number;
  owned: WeeklyDrop[];
  missed: WeeklyDrop[];
  fullVault: boolean;
  isLegacyMember: boolean;
};

export type LibraryNextDropCacheEntry = {
  nextDrop: WeeklyDrop;
  nextReleaseAt: string;
};

type LibraryNextDropStorageEntry = {
  version: 1;
  cachedAt: number;
  seriesSlug: string;
  nextDrop: CachedWeeklyDrop;
  nextReleaseAt: string;
};

const memoryNextDropCache = new Map<string, LibraryNextDropCacheEntry>();

function serializeVideo(video: FirebaseVideo): CachedFirebaseVideo {
  return {
    id: video.id,
    slug: video.slug,
    title: video.title,
    thumbnailUrl: video.thumbnailUrl,
    creatorImageUrl: video.creatorImageUrl,
    publishedAtIso: video.publishedAtIso,
    durationLabel: video.durationLabel,
    creator: {
      id: video.creator.id,
      name: video.creator.name,
      slug: video.creator.slug,
      image: video.creator.image,
    },
  };
}

function deserializeVideo(cached: CachedFirebaseVideo): FirebaseVideo {
  return {
    id: cached.id,
    slug: cached.slug,
    title: cached.title,
    status: "public",
    duration: 0,
    creator: {
      id: cached.creator.id,
      name: cached.creator.name,
      slug: cached.creator.slug,
      image: cached.creator.image ?? "",
    },
    thumbnailUrl: cached.thumbnailUrl,
    creatorImageUrl: cached.creatorImageUrl,
    publishedAtIso: cached.publishedAtIso,
    durationLabel: cached.durationLabel,
  } as unknown as FirebaseVideo;
}

export function serializeWeeklyDrop(drop: WeeklyDrop): CachedWeeklyDrop {
  return {
    seriesId: drop.seriesId,
    week: drop.week,
    cycle: drop.cycle,
    slotIndex: drop.slotIndex,
    slug: drop.slug,
    title: drop.title,
    thumbnail: drop.thumbnail,
    duration: drop.duration,
    releaseAt: drop.releaseAt,
    tag: drop.tag,
    status: drop.status,
    video: serializeVideo(drop.video),
  };
}

export function deserializeWeeklyDrop(cached: CachedWeeklyDrop): WeeklyDrop {
  return {
    seriesId: cached.seriesId,
    week: cached.week,
    cycle: cached.cycle,
    slotIndex: cached.slotIndex,
    slug: cached.slug,
    title: cached.title,
    thumbnail: cached.thumbnail,
    duration: cached.duration,
    releaseAt: cached.releaseAt,
    tag: cached.tag,
    status: cached.status,
    video: deserializeVideo(cached.video),
  };
}

export function buildLibraryFingerprint(input: LibraryFingerprintInput): string {
  const creatorsKey =
    input.creators === "all" ? "all" : [...input.creators].sort().join(",");
  return JSON.stringify({
    uid: input.uid,
    isLegacyMember: input.isLegacyMember,
    membershipType: input.membershipType ?? null,
    creators: creatorsKey,
    v2Tier: input.v2Tier ?? null,
    subscribedAtMs: input.subscribedAtMs,
    devState: input.devState,
    subscribedWeeks: input.subscribedWeeks,
  });
}

export function readWeeklyDropsCache(): WeeklyDrop[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(WEEKLY_DROPS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WeeklyDropsCacheEntry;
    if (parsed?.version !== 1 || !Array.isArray(parsed.drops)) return null;
    if (Date.now() - parsed.cachedAt > WEEKLY_DROPS_TTL_MS) return null;
    return parsed.drops.map(deserializeWeeklyDrop);
  } catch {
    return null;
  }
}

export function writeWeeklyDropsCache(drops: WeeklyDrop[]): void {
  if (typeof window === "undefined") return;
  try {
    const entry: WeeklyDropsCacheEntry = {
      version: 1,
      cachedAt: Date.now(),
      drops: drops.map(serializeWeeklyDrop),
    };
    window.localStorage.setItem(WEEKLY_DROPS_KEY, JSON.stringify(entry));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Synchronous read: in-memory first, then localStorage (for instant library banner). */
export function readLibraryNextDropCache(
  seriesSlug: string,
): LibraryNextDropCacheEntry | null {
  const memory = memoryNextDropCache.get(seriesSlug);
  if (memory) return memory;

  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LIBRARY_NEXT_DROP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LibraryNextDropStorageEntry;
    if (
      parsed?.version !== 1 ||
      parsed.seriesSlug !== seriesSlug ||
      !parsed.nextDrop ||
      typeof parsed.nextReleaseAt !== "string"
    ) {
      return null;
    }
    if (Date.now() - parsed.cachedAt > WEEKLY_DROPS_TTL_MS) return null;
    const entry: LibraryNextDropCacheEntry = {
      nextDrop: deserializeWeeklyDrop(parsed.nextDrop),
      nextReleaseAt: parsed.nextReleaseAt,
    };
    memoryNextDropCache.set(seriesSlug, entry);
    return entry;
  } catch {
    return null;
  }
}

export function writeLibraryNextDropCache(
  seriesSlug: string,
  nextDrop: WeeklyDrop,
  nextReleaseAt: string,
): void {
  const entry: LibraryNextDropCacheEntry = { nextDrop, nextReleaseAt };
  memoryNextDropCache.set(seriesSlug, entry);

  if (typeof window === "undefined") return;
  try {
    const payload: LibraryNextDropStorageEntry = {
      version: 1,
      cachedAt: Date.now(),
      seriesSlug,
      nextDrop: serializeWeeklyDrop(nextDrop),
      nextReleaseAt,
    };
    window.localStorage.setItem(LIBRARY_NEXT_DROP_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readLibraryOwnedCache(
  uid: string,
  fingerprint: string,
): LibraryOwnedCacheEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${LIBRARY_OWNED_KEY_PREFIX}${uid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      version: number;
      fingerprint: string;
      cachedAt: number;
      owned: CachedWeeklyDrop[];
      missed: CachedWeeklyDrop[];
      fullVault: boolean;
      isLegacyMember: boolean;
    };
    if (parsed?.version !== 1 || parsed.fingerprint !== fingerprint) return null;
    if (!Array.isArray(parsed.owned) || !Array.isArray(parsed.missed)) return null;
    return {
      version: 1,
      fingerprint: parsed.fingerprint,
      cachedAt: parsed.cachedAt,
      owned: parsed.owned.map(deserializeWeeklyDrop),
      missed: parsed.missed.map(deserializeWeeklyDrop),
      fullVault: !!parsed.fullVault,
      isLegacyMember: !!parsed.isLegacyMember,
    };
  } catch {
    return null;
  }
}

export function writeLibraryOwnedCache(
  uid: string,
  entry: Omit<LibraryOwnedCacheEntry, "version" | "cachedAt">,
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: LibraryOwnedCacheEntry = {
      version: 1,
      cachedAt: Date.now(),
      ...entry,
    };
    window.localStorage.setItem(
      `${LIBRARY_OWNED_KEY_PREFIX}${uid}`,
      JSON.stringify({
        ...payload,
        owned: payload.owned.map(serializeWeeklyDrop),
        missed: payload.missed.map(serializeWeeklyDrop),
      }),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearLibraryCaches(uid?: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(WEEKLY_DROPS_KEY);
    window.localStorage.removeItem(LIBRARY_NEXT_DROP_KEY);
    memoryNextDropCache.clear();
    if (uid) {
      window.localStorage.removeItem(`${LIBRARY_OWNED_KEY_PREFIX}${uid}`);
    }
  } catch {
    /* ignore */
  }
}
